/**
 * El tema de la interfaz.
 *
 * Tres opciones: claro, oscuro o lo que diga el sistema. La eleccion se guarda
 * en el navegador de cada persona y no en la base: es una preferencia de este
 * dispositivo, no del negocio ni de la cuenta.
 *
 * Quien resuelve "sistema" a un color concreto es el navegador, y el resultado
 * se estampa en `<html data-theme>`. Todo el CSS mira ese atributo y ninguna
 * regla depende ya de `prefers-color-scheme`, asi que cambiar de tema no
 * necesita recargar y la eleccion le gana al sistema operativo.
 *
 * El primer estampado no lo hace este modulo sino el script en linea de
 * `index.html`, que corre antes del primer pintado: si esperara a React, la
 * pagina apareceria clara y saltaria a oscura a la vista de todos.
 */

import { useCallback, useSyncExternalStore } from "react";

/** Lo que elige la persona. */
export type Tema = "claro" | "oscuro" | "sistema";

/** El color con el que termina pintandose, ya resuelto. */
export type TemaResuelto = "claro" | "oscuro";

export const TEMAS: readonly Tema[] = ["claro", "oscuro", "sistema"];

/** La misma clave que usa el script en linea de `index.html`. */
export const CLAVE_TEMA = "turnpro:tema";

const CONSULTA_OSCURO = "(prefers-color-scheme: dark)";

function esTema(valor: string | null): valor is Tema {
  return valor === "claro" || valor === "oscuro" || valor === "sistema";
}

/**
 * Lo guardado, o "sistema".
 *
 * `localStorage` no siempre esta: en una ventana privada o con el sitio
 * bloqueado, leerlo tira. Sin preferencia guardada la respuesta es la misma que
 * sin haber elegido nunca.
 */
function leerGuardado(): Tema {
  try {
    const valor = localStorage.getItem(CLAVE_TEMA);
    return esTema(valor) ? valor : "sistema";
  } catch {
    return "sistema";
  }
}

function guardar(tema: Tema): void {
  try {
    // "sistema" es la ausencia de eleccion: se borra en lugar de guardarse,
    // para que quien nunca eligio y quien volvio al sistema queden igual.
    if (tema === "sistema") localStorage.removeItem(CLAVE_TEMA);
    else localStorage.setItem(CLAVE_TEMA, tema);
  } catch {
    // Sin almacenamiento el tema vale para esta pestaña y no se recuerda. Es
    // peor que recordarlo, pero mucho mejor que no dejar cambiarlo.
  }
}

function sistemaEsOscuro(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia(CONSULTA_OSCURO).matches;
}

export function resolver(tema: Tema): TemaResuelto {
  if (tema === "sistema") return sistemaEsOscuro() ? "oscuro" : "claro";
  return tema;
}

/* -------------------------------------------------------------------------- */
/* El estado, fuera de React                                                   */
/* -------------------------------------------------------------------------- */

/*
 * El tema lo necesitan piezas que no cuelgan unas de otras — el selector, la
 * barra del navegador, cualquier pantalla — y cambia por dos motivos distintos:
 * porque alguien lo eligio, o porque el sistema operativo cambio de color con
 * la app abierta. Un store con `useSyncExternalStore` cubre los dos casos sin
 * envolver el arbol en otro proveedor.
 */

let actual: Tema = typeof window === "undefined" ? "sistema" : leerGuardado();
const suscriptores = new Set<() => void>();

function avisar(): void {
  for (const suscriptor of suscriptores) suscriptor();
}

/**
 * El color de la barra del navegador, por tema.
 *
 * Son los mismos valores que `--surface` en `index.css`, escritos aca porque
 * `meta[name=theme-color]` no entiende variables CSS. Si cambian alla, cambian
 * aca.
 */
const COLOR_DE_BARRA = { claro: "#ffffff", oscuro: "#070709" } as const;

/**
 * Estampa el tema resuelto en `<html>`, que es lo que mira todo el CSS, y de
 * paso pinta la barra del navegador.
 *
 * Lo segundo no se puede resolver con `<meta media="(prefers-color-scheme)">`
 * como hace la landing: aca el tema lo decide la persona, asi que quien elige
 * oscuro con el sistema en claro veria la app oscura dentro de una barra
 * blanca.
 */
function aplicar(): void {
  if (typeof document === "undefined") return;

  const resuelto = resolver(actual);
  document.documentElement.dataset["theme"] = resuelto === "oscuro" ? "dark" : "light";

  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", COLOR_DE_BARRA[resuelto]);
}

/*
 * El atributo de `<html>` ya lo estampo el script en linea antes del primer
 * pintado; la barra del navegador, no. Se aplica al cargar el modulo para que
 * el color de arriba coincida con el de la pagina desde el principio.
 */
aplicar();

function suscribir(alCambiar: () => void): () => void {
  suscriptores.add(alCambiar);

  // Con el tema en "sistema", que el sistema operativo cambie de color es un
  // cambio de tema aunque nadie haya tocado nada.
  const consulta =
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(CONSULTA_OSCURO)
      : null;

  const alCambiarElSistema = (): void => {
    if (actual !== "sistema") return;
    aplicar();
    avisar();
  };

  consulta?.addEventListener("change", alCambiarElSistema);

  return () => {
    suscriptores.delete(alCambiar);
    consulta?.removeEventListener("change", alCambiarElSistema);
  };
}

export function setTema(tema: Tema): void {
  actual = tema;
  guardar(tema);
  aplicar();
  avisar();
}

function leerTema(): Tema {
  return actual;
}

function leerTemaEnServidor(): Tema {
  return "sistema";
}

/** El tema elegido y como cambiarlo. */
export function useTema(): {
  tema: Tema;
  resuelto: TemaResuelto;
  cambiar: (tema: Tema) => void;
} {
  const tema = useSyncExternalStore(suscribir, leerTema, leerTemaEnServidor);
  const cambiar = useCallback((siguiente: Tema) => setTema(siguiente), []);
  return { tema, resuelto: resolver(tema), cambiar };
}

/** Atajo para lo que solo necesita saber de que color se esta pintando. */
export function useTemaOscuro(): boolean {
  return useTema().resuelto === "oscuro";
}
